import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Form,
  Modal,
  Select,
  Space,
  Spin,
} from "antd";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { onError } from "src/common/onError";
import {
  createItem2,
  getComponentEditor,
} from "src/pages/EntityCardPage/blockBuilder/createItem2";
import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { edValue2EntityField } from "src/common/attrEdit/transforms";
import { hasPermissionIn, Permission } from "src/types/Permission";
import { getEditorInfo } from "src/common/attrEdit";
import { ContextCommon, FormWithBlockStore } from "../FormWithBlocks";
import { createMassEditAttrStore, doMassEdit } from "./MassEditAttrStore";
import {
  MassEditFieldsNames,
  MassEditFormData,
  MassEditRequestData,
} from "./types";

const formData2Request = (
  formData: MassEditFormData,
  entitiesIds: ZUid[],
  attribute: ZAttribute,
): MassEditRequestData => ({
  entitiesIds,
  clear: !!formData[MassEditFieldsNames.clear],
  attributeValue: edValue2EntityField(
    attribute,
    formData[MassEditFieldsNames.values],
  ),
});

const initialFormData: Partial<MassEditFormData> = {
  attributeId: undefined,
  clear: false,
  values: undefined,
};

interface PropsInnerForm {
  selectedAttr: ZAttribute;
  objectId: ZUid;
  typesMap: Record<number, string>;
  onCancel: () => void;
  onSubmit: (data: MassEditFormData) => void;
}

const InnerForm: React.FC<PropsInnerForm> = ({
  selectedAttr,
  objectId,
  typesMap,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [formStore] = React.useState(new FormWithBlockStore());

  const ctx: ContextCommon = React.useMemo(
    () => ({
      store: formStore,
      prevName: [],
    }),
    [formStore],
  );

  React.useEffect(() => {
    form.resetFields([MassEditFieldsNames.values, MassEditFieldsNames.clear]);

    // LPLM-5029. Для Checkbox нужно устанавливать по-дефолту false.
    // Потому что чекбокс одинаково выглядит для undefined и false. С другими компонентами такой проблемы пока нет :)
    const editorInfo = getEditorInfo(selectedAttr.viewStyles);
    const component = getComponentEditor(editorInfo, typesMap, selectedAttr);
    if (component?.editor === "Checkbox") {
      form.setFieldValue(MassEditFieldsNames.values, ["false"]);
    }
  }, [selectedAttr]);

  // Я понятия не имею, зачем им это
  const consistForm = (changed: Partial<MassEditFormData>) => {
    if (
      MassEditFieldsNames.values in changed &&
      changed[MassEditFieldsNames.values] &&
      changed[MassEditFieldsNames.values].length > 0
    ) {
      form.resetFields([MassEditFieldsNames.clear]);
    } else if (
      MassEditFieldsNames.clear in changed &&
      changed[MassEditFieldsNames.clear]
    ) {
      form.resetFields([MassEditFieldsNames.values]);
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      onValuesChange={consistForm}
      initialValues={initialFormData}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        {selectedAttr && (
          <>
            <Form.Item label="Значение" name={MassEditFieldsNames.values}>
              {createItem2(selectedAttr, {
                objectId,
                typesMap,
                canUpdate: true,
                restrictValues: false,
              }).render(ctx)}
            </Form.Item>

            <Form.Item name={MassEditFieldsNames.clear} valuePropName="checked">
              <Checkbox>Очистить</Checkbox>
            </Form.Item>
          </>
        )}
        <Flex justify="end" gap={15} align="center">
          <Button htmlType="button" onClick={onCancel}>
            Отменить
          </Button>
          <Button htmlType="submit" type="primary">
            Применить
          </Button>
        </Flex>
      </Space>
    </Form>
  );
};

interface PropsMassEditModal {
  attrList: ZAttribute[];
  objectId: ZUid;
  entitiesIds: ZUid[];
  typesMap: Record<number, string>;
  onCancel: () => void;
  onSuccess: () => void;
}

export const MassEditModal: React.FC<PropsMassEditModal> = observer(
  ({ attrList, entitiesIds, objectId, typesMap, onCancel, onSuccess }) => {
    const store = useMemo(() => createMassEditAttrStore(), []);
    const [selectedAttr, setSelectedAttr] = React.useState<ZAttribute | null>(
      null,
    );
    const canUpdate = selectedAttr
      ? hasPermissionIn(selectedAttr, Permission.attrUpdate)
      : true;

    const attrsOptions = attrList.map((attr) => ({
      label: attr.name,
      value: attr.id,
    }));
    const selectAttr = (v: string) => {
      const attr2Edit = attrList.find((attr) => attr.id === v);
      setSelectedAttr(attr2Edit ?? null);
    };
    const onSubmit = async (data: MassEditFormData) => {
      store.setFormData(data);
    };
    const onConfirm = async () => {
      if (store.formData && selectedAttr) {
        store.setLoading(true);
        doMassEdit(formData2Request(store.formData, entitiesIds, selectedAttr))
          .finally(() => store.setLoading(false))
          .then(() => {
            store.setFormData(undefined);
            onSuccess();
          })
          .catch(onError);
      }
    };
    const doCancel = () => {
      store.setFormData(undefined);
      onCancel();
    };

    const InnerConfirm = () => (
      <Spin spinning={store.loading}>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            Массовая замена значений затронет экземпляры объекта:{" "}
            {entitiesIds.length}
          </div>
          <Flex justify="end" gap={15} align="center">
            <Button htmlType="button" onClick={doCancel}>
              Отменить
            </Button>
            <Button type="primary" onClick={onConfirm}>
              Применить
            </Button>
          </Flex>
        </Space>
      </Spin>
    );

    return (
      <Modal
        open
        closable
        title="Замена значения"
        onCancel={doCancel}
        footer={null}
      >
        {store.formData === undefined ? (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Form.Item labelCol={{ span: 24 }} label="Атрибут" colon>
              <Select
                style={{ width: "100%" }}
                options={attrsOptions}
                onChange={selectAttr}
              />
            </Form.Item>
            {!canUpdate && (
              <Alert
                type="error"
                description="У пользователя нет прав для работы с этим атрибутом. Обратитесь к администратору для получения необходимых прав."
              />
            )}
            {selectedAttr && canUpdate && (
              <InnerForm
                selectedAttr={selectedAttr}
                objectId={objectId}
                typesMap={typesMap}
                onCancel={doCancel}
                onSubmit={onSubmit}
              />
            )}
          </Space>
        ) : (
          <InnerConfirm />
        )}
      </Modal>
    );
  },
);
